import React from "react";
import { type Mode, type Theme } from "~/features/ui/get-theme-style";
import { hasItems, padNumber, takeFirst } from "~/features/app/utils";
import { Eyebrow } from "~/features/ui/eyebrow";
import { AdaptiveSection } from "./adaptive-section";
import { useEditorialCardsFilterBy } from "./use-editorial-cards-filter";
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
export type EditorialCardsCardLayout = {
  index: number;
  featured: boolean;
  cardSize: "regular" | "large";
  ratio: "16/9" | "3/2";
  count?: string;
};
export type EditorialCardsBlockViewProps<TCard = unknown> = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  cards?: TCard[] | null;
  eyebrow?: string | null;
  headline?: string | null;
  text?: string | null;
  link?: React.ReactNode;
  hasFilter?: boolean | null;
  hasTopDivider?: boolean | null;
  isHighlighted?: boolean | null;
  mode?: string | null;
  theme?: string | null;
  getCardKey?: (card: TCard, index: number) => React.Key;
  getCardType: (card: TCard) => string | null | undefined;
  isShowcaseCard?: (card: TCard) => boolean;
  renderCard: (card: TCard, layout: EditorialCardsCardLayout) => React.ReactNode;
  typeLabels: Record<string, string>;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  /** Left-align the (desktop) headline instead of the default bottom-right placement. */
  headlineStart?: boolean;
};
export function EditorialCardsBlockView<TCard = unknown>({
  anchor,
  cards = [],
  eyebrow,
  headline,
  text,
  link,
  hasFilter = false,
  hasTopDivider = true,
  isHighlighted = false,
  mode = "light",
  theme = "white",
  getCardKey,
  getCardType,
  isShowcaseCard,
  renderCard,
  typeLabels,
  sectionProps,
  headlineStart
}: EditorialCardsBlockViewProps<TCard>) {
  const entries = cards ?? [];
  const eyebrowNode = eyebrow ? <Eyebrow text={eyebrow} mode={mode as Mode} /> : undefined;
  const {
    filteredCards,
    filterBar,
    activeType
  } = useEditorialCardsFilterBy<TCard>(entries, hasFilter as boolean, getCardType, typeLabels);
  if (!hasItems(entries)) {
    return null;
  }
  const hasShowcaseCards = filteredCards?.some(c => isShowcaseCard?.(c));
  const hasHeaderData = Boolean(eyebrow || headline || text || link || filterBar);
  const cardSize = entries.length === 1 || !hasHeaderData ? "large" : "regular";
  const ratio: "16/9" | "3/2" = entries.length === 1 && !hasHeaderData ? "16/9" : "3/2";
  const featured = entries.length === 1;
  return <AdaptiveSection {...sectionProps} mode={mode as Mode} theme={theme as Theme} eyebrow={eyebrowNode} headline={headline || undefined} link={link} text={text || undefined} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} hasTopDivider={hasTopDivider ?? true} isHighlighted={isHighlighted ?? false} filterBar={filterBar} filterKey={activeType} perPage={hasShowcaseCards ? 4 : 3} hasShowcaseCards={hasShowcaseCards} headlineStart={headlineStart}>
      {filteredCards?.map((card, idx) => <React.Fragment key={getCardKey?.(card, idx) ?? idx}>
          {renderCard(card, {
        index: idx,
        featured,
        cardSize,
        ratio,
        count: padNumber(idx + 1)
      })}
        </React.Fragment>)}
    </AdaptiveSection>;
}